<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SlideOver from '@/components/shared/SlideOver.vue'
import { useOrganisationInvoiceChannel } from '@/composables/useOrganisationInvoiceChannel'
import { InvoiceDeliveryChannel } from '@/types/invoicing'
import type { OrganisationInvoiceChannel, OrganisationInvoicingSummary } from '@/types/invoicing'

const MIN_REASON_LENGTH = 10
const MAX_REASON_LENGTH = 500

type ChannelChoice = 'automatic' | InvoiceDeliveryChannel

const props = defineProps<{
  open: boolean
  organisationId: number
  current: OrganisationInvoicingSummary
}>()

const emit = defineEmits<{
  close: []
  updated: [channel: OrganisationInvoiceChannel]
}>()

const { t } = useI18n()
const { saving, actionError, fieldErrors, update } = useOrganisationInvoiceChannel()

const choice = ref<ChannelChoice>('automatic')
const reason = ref('')
const localReasonError = ref<string | null>(null)

const peppolAvailable = computed(() => props.current.peppol_identifier !== null)
const channelError = computed(() => fieldErrors.value['channel']?.[0] ?? null)
const reasonError = computed(() => fieldErrors.value['reason']?.[0] ?? localReasonError.value)

function channelLabel(channel: InvoiceDeliveryChannel): string {
  return channel === InvoiceDeliveryChannel.Peppol
    ? t('admin.invoicing.channel_peppol')
    : t('admin.invoicing.channel_email')
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    choice.value = props.current.delivery_channel_override ?? 'automatic'
    reason.value = ''
    localReasonError.value = null
    fieldErrors.value = {}
    actionError.value = null
  },
)

function handleClose(): void {
  if (saving.value) return
  emit('close')
}

async function handleSubmit(): Promise<void> {
  const trimmed = reason.value.trim()
  if (trimmed.length < MIN_REASON_LENGTH || trimmed.length > MAX_REASON_LENGTH) {
    localReasonError.value = t('admin.invoicing.org_error_reason')
    return
  }
  localReasonError.value = null

  // "Automatic" clears the override (null); the server derives the channel from the VAT number again.
  const saved = await update(props.organisationId, {
    channel: choice.value === 'automatic' ? null : choice.value,
    reason: trimmed,
  })
  if (saved !== null) {
    emit('updated', saved)
    emit('close')
  }
}
</script>

<template>
  <SlideOver :open="open" :title="t('admin.invoicing.org_slide_title')" :saving="saving" @close="handleClose">
    <div
      v-if="actionError"
      class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300"
      role="alert"
    >
      {{ t(actionError) }}
    </div>

    <fieldset class="space-y-3">
      <legend class="text-sm font-medium text-slate-300 mb-1">{{ t('admin.invoicing.org_current') }}</legend>

      <label class="flex items-center gap-2 text-sm text-slate-300">
        <input
          v-model="choice"
          type="radio"
          value="automatic"
          name="invoice-channel"
          class="border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
          data-testid="channel-automatic"
        />
        {{ t('admin.invoicing.org_automatic', { channel: channelLabel(current.derived_channel) }) }}
      </label>

      <label class="flex items-center gap-2 text-sm text-slate-300">
        <input
          v-model="choice"
          type="radio"
          :value="InvoiceDeliveryChannel.Email"
          name="invoice-channel"
          class="border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
          data-testid="channel-email"
        />
        {{ t('admin.invoicing.channel_email') }}
      </label>

      <div>
        <label class="flex items-center gap-2 text-sm" :class="peppolAvailable ? 'text-slate-300' : 'text-slate-500'">
          <input
            v-model="choice"
            type="radio"
            :value="InvoiceDeliveryChannel.Peppol"
            name="invoice-channel"
            :disabled="!peppolAvailable"
            class="border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500 disabled:opacity-50"
            data-testid="channel-peppol"
          />
          {{ t('admin.invoicing.channel_peppol') }}
        </label>
        <p v-if="!peppolAvailable" class="mt-1 ml-6 text-xs text-slate-500">
          {{ t('admin.invoicing.org_peppol_requires_vat') }}
        </p>
      </div>

      <p v-if="channelError" class="text-xs text-red-400">{{ channelError }}</p>
    </fieldset>

    <div>
      <label for="invoice-channel-reason" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('admin.invoicing.org_reason_label') }}
      </label>
      <textarea
        id="invoice-channel-reason"
        v-model="reason"
        rows="3"
        :maxlength="MAX_REASON_LENGTH"
        class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        :class="{ 'border-red-500': reasonError }"
      />
      <div class="mt-1 flex items-center justify-between">
        <p v-if="reasonError" class="text-xs text-red-400">{{ reasonError }}</p>
        <p v-else class="text-xs text-slate-500">{{ t('admin.invoicing.org_reason_help') }}</p>
        <p class="text-xs text-slate-500 shrink-0 ml-2">{{ reason.length }}/{{ MAX_REASON_LENGTH }}</p>
      </div>
    </div>

    <p class="text-xs text-slate-400">{{ t('admin.invoicing.org_applies_note') }}</p>

    <template #footer>
      <button
        type="button"
        :disabled="saving"
        class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
        @click="handleClose"
      >
        {{ t('admin.common.cancel') }}
      </button>
      <button
        type="button"
        :disabled="saving"
        class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        data-testid="channel-submit"
        @click="handleSubmit"
      >
        {{ saving ? t('admin.common.loading') : t('admin.common.save') }}
      </button>
    </template>
  </SlideOver>
</template>
